// TODO(api): Real backend integration. Currently scaffolded only.
// Maps the server error envelope { error: { code, message, fields, meta } } to
// NoticeApiError. Activated by setting VITE_USE_MOCK_API=false; see
// notices.adapter.ts. Endpoint shapes follow docs/api-contracts/notice-management.md.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';

import {
  NoticeApiError,
  type CreateNoticeInput,
  type Notice,
  type NoticeApi,
  type NoticeApiErrorCode,
  type NoticeSummary,
  type UpdateNoticeInput,
} from '../types/notices.types';

interface ApiEnvelope<T> {
  data: T;
}

interface ServerErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string>;
    meta?: Record<string, unknown>;
  };
}

function mapError(e: unknown): never {
  // Already a typed error raised inside the try (e.g. the list shape guard) —
  // rethrow untouched, or it would be re-labelled below as a network failure.
  if (e instanceof NoticeApiError) {
    throw e;
  }
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  // SHCT-669 — axios rejects with NO `response` when the request never reached
  // the server (offline / DNS / CORS). Without this branch it fell through to
  // UNKNOWN + "알 수 없는 오류가 발생했습니다.", hiding the real cause. The i18n
  // `errors.NETWORK` key is what actually renders; this string is the fallback.
  if (!candidate.response) {
    throw new NoticeApiError('NETWORK', '네트워크 연결을 확인해주세요.');
  }
  const payload = candidate.response.data?.error;
  throw new NoticeApiError(
    (payload?.code as NoticeApiErrorCode) ?? 'UNKNOWN',
    payload?.message ?? '알 수 없는 오류가 발생했습니다.',
    { fields: payload?.fields, meta: payload?.meta },
  );
}

const noticePath = (id: number): string => `${endpoints.notices}/${id}`;

export const noticesHttpApi: NoticeApi = {
  async list(): Promise<NoticeSummary[]> {
    try {
      const res = await httpClient.get<ApiEnvelope<NoticeSummary[]>>(endpoints.notices);
      const data = res.data?.data;
      // List table iterates this; a non-array body (wrong envelope, HTML, paged
      // wrapper) must surface as a load error rather than white-screen the table.
      if (!Array.isArray(data)) {
        throw new NoticeApiError('UNKNOWN', '목록을 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async get(id: number): Promise<Notice> {
    try {
      const res = await httpClient.get<ApiEnvelope<Notice>>(noticePath(id));
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async create(input: CreateNoticeInput): Promise<Notice> {
    try {
      const res = await httpClient.post<ApiEnvelope<Notice>>(endpoints.notices, input);
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async update(id: number, input: UpdateNoticeInput): Promise<Notice> {
    try {
      const res = await httpClient.patch<ApiEnvelope<Notice>>(noticePath(id), input);
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  // Contract §3.5: 204 on success; FE just toasts and invalidates queries.
  async remove(id: number): Promise<void> {
    try {
      await httpClient.delete(noticePath(id));
    } catch (e) {
      mapError(e);
    }
  },
};
